import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { get } from '@truefit/http-utils';
import useServerState, {
  DEFAULT_AUTO_DIRECTOR_TUNING,
  ServerStateAndFunctions,
} from '../hooks/useServerState';
import useAutoDirector, { AutoDirectorTuning } from '../hooks/websocket/useAutoDirector';
import { useAuth, LocalSessionState } from '@/features/scaffolding/components/AuthContext';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import Tracking from '@/shared/services/tracking';

type Props = {
  children: ReactNode;
  // The open game's uniqueId. Auto-director tuning can differ per league, so
  // the defaults are resolved for this game.
  gameId?: string;
};

// Both edges of the timed event have to agree on the name or the close never
// finds the open entry, so it is written once.
const AUTO_DIRECTOR_SESSION_EVENT = 'Auto-Director Session';

// Create a default/initial state that matches ServerStateAndFunctions type
export const ServerStateContext = React.createContext<ServerStateAndFunctions | null>(null);

const ServerStateProvider = ({ children, gameId }: Props) => {
  const serverState = useServerState();
  const { sessionState } = useAuth();

  // Ops-owned auto-director tuning, from the 'interactive' profile assigned to
  // this game's league, else the mode's default. The response is a partial
  // override, so it merges over the built-in tuning: nothing assigned sends
  // `{}` and leaves it untouched.
  // This lives here rather than in useServerState because the hook is also
  // called directly per ClipGroupItem, which would fan the request out.
  const [autoDirectorDefaults, setAutoDirectorDefaults] = useState<AutoDirectorTuning>(
    DEFAULT_AUTO_DIRECTOR_TUNING,
  );
  // The game the tuning above was resolved for, whether the fetch succeeded or
  // not, so starting auto-director at game load waits for this game's profile.
  const [autoDirectorDefaultsGameId, setAutoDirectorDefaultsGameId] = useState<string>();

  useEffect(() => {
    // Drop the previous game's tuning first: another league's profile must not
    // carry over while this game's request is in flight, or if it fails.
    setAutoDirectorDefaults(DEFAULT_AUTO_DIRECTOR_TUNING);

    if (sessionState !== LocalSessionState.READY || !gameId) return undefined;

    let cancelled = false;

    get<Partial<AutoDirectorTuning>>(`/v1.0/games/${gameId}/auto-director-config`)
      .then((response) => {
        if (cancelled) return;
        setAutoDirectorDefaults({ ...DEFAULT_AUTO_DIRECTOR_TUNING, ...response.data });
      })
      .catch((error) => {
        // Keeping the built-in tuning is the pre-profile behaviour, so a failed
        // fetch must not stop the user from turning auto-director on.
        console.error('[ServerStateProvider] Error loading auto-director defaults:', error);
      })
      .finally(() => {
        if (!cancelled) setAutoDirectorDefaultsGameId(gameId);
      });

    return () => {
      cancelled = true;
    };
  }, [sessionState, gameId]);

  // A game opens on Auto-Director. It is turned on once per game, so a camera
  // the user picks afterwards sticks, and only once:
  // - this game's switch is done: until then the session's player still runs
  //   the previous game, which would take the command instead;
  // - this game's tuning is resolved, since the enable pushes it;
  // - the player reports an auto_director block (a single-camera rig has none).
  // It is sent even when the player already reports auto-director on, as that
  // report can still be the previous game's.
  const { gameSwitched, game } = useCurrentGame();
  const { enableAutoDirector } = useAutoDirector();
  const autoDirectorStartedForRef = useRef<string | undefined>(undefined);
  const { autoDirectorAvailable } = serverState;

  useEffect(() => {
    if (!gameId || autoDirectorStartedForRef.current === gameId) return;
    if (!gameSwitched || autoDirectorDefaultsGameId !== gameId || !autoDirectorAvailable) return;

    autoDirectorStartedForRef.current = gameId;
    // Auto-Director follows the tracked object, so with tracking off the game
    // keeps the player's camera.
    if (game.enableTracking === false) return;

    enableAutoDirector(autoDirectorDefaults);
  }, [
    gameId,
    gameSwitched,
    autoDirectorDefaultsGameId,
    autoDirectorAvailable,
    game.enableTracking,
    autoDirectorDefaults,
    enableAutoDirector,
  ]);

  // Time spent in auto-director, as a timed event: the opening call only starts
  // a clock, and the eviction call is what emits, carrying the `duration` the
  // tracking service measures between the two.
  //
  // It keys off the player's reported state rather than the buttons, so every
  // way out is counted with one observer — a camera pick (which is the
  // dominant one, since `selectCamera` disables auto-director for the toolbar,
  // the keyboard shortcut, the surface click and the fallback alike), the
  // camera-row toggle, the tuning panel's Switch, and anything the player
  // decides on its own. The cleanup also runs on unmount, so leaving the game
  // page closes the session; `pagehide` is already drained by the service.
  const { autoDirectorEnabled } = serverState;

  useEffect(() => {
    if (!autoDirectorEnabled) return undefined;

    Tracking.getInstance().timedEvent(AUTO_DIRECTOR_SESSION_EVENT, { category: 'Playback' });

    return () => {
      Tracking.getInstance().timedEvent(AUTO_DIRECTOR_SESSION_EVENT, {}, true);
    };
  }, [autoDirectorEnabled]);

  const value = useMemo<ServerStateAndFunctions>(
    () => ({ ...serverState, autoDirectorDefaults }),
    [serverState, autoDirectorDefaults],
  );

  return <ServerStateContext.Provider value={value}>{children}</ServerStateContext.Provider>;
};

export default ServerStateProvider;
